"use client";

import { Button } from "@pex/ui-web";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { IconBoltColour } from "@/components/icons/IconBoltColour";
import { IconLeftClickColour } from "@/components/icons/IconLeftClickColour";
import { IconVerifiedColour } from "@/components/icons/IconVerifiedColour";
import { ScrollLink } from "@/components/scroll-link";

const STEPS = [
  {
    icon: <IconLeftClickColour height={48} width={48} />,
    name: "Sign Up",
    description:
      "Choose a plan that fits your organisation\u2019s needs and create your profile on Bridge",
  },
  {
    icon: <IconVerifiedColour height={48} width={48} />,
    name: "Get Certified",
    description:
      "Complete online training at your own pace through our comprehensive Academy",
  },
  {
    icon: <IconBoltColour height={48} width={48} />,
    name: "Launch",
    description:
      "Use our detailed guides and resources to start your recovery groups or programmes",
  },
] as const;

export function ThreeStepsTimeline() {
  const timelineRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const el = timelineRef.current;
    if (!el) {
      return;
    }

    const updateProgress = () => {
      const { top, height } = el.getBoundingClientRect();
      const windowH = window.innerHeight;
      // Start when the timeline column enters from the bottom of the viewport.
      // Finish when the bottom of the timeline column reaches 30% from the top
      // of the viewport — guaranteeing the user sees the full animation.
      const scrolled = windowH - top; // distance section has travelled up
      const total = height + windowH * 0.5; // spread animation over section + 50% viewport
      setProgress(Math.max(0, Math.min(1, scrolled / total)));
    };

    window.addEventListener("scroll", updateProgress, { passive: true });
    // Run once immediately in case section is already in view on load
    updateProgress();
    return () => window.removeEventListener("scroll", updateProgress);
  }, []);

  return (
    <section className="bg-white py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 md:grid-cols-2 md:gap-20">
          {/* Left — heading + CTAs */}
          <div className="space-y-8 md:pt-2">
            <h2 className="heading-2 font-bold">
              From Vision to Implementation in Just Three Steps
            </h2>
            <div className="flex flex-wrap items-center gap-3">
              <Button asChild variant="secondary-hybrid">
                <ScrollLink href="#getting-started_pricing">
                  View Pricing
                </ScrollLink>
              </Button>
              <Button asChild variant="link-icon">
                <Link
                  href="https://www.youtube.com/watch?v=sk3iE6-hrh8"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Watch: How It Works (5 min)
                  <ArrowRight />
                </Link>
              </Button>
            </div>
          </div>

          {/* Right — vertical timeline with scroll-driven progress bar */}
          <div className="relative" ref={timelineRef}>
            {/*
              Track line: starts at the vertical centre of the first icon (top-10 = 40px)
              and ends at the vertical centre of the last icon (bottom-10 = 40px from bottom).
              This prevents the line overshooting past the Launch icon.
            */}
            <div className="absolute top-10 bottom-10 left-10 w-px bg-border" />
            {/* Animated fill — height grows from 0 to the full track length as the user scrolls */}
            <div
              className="absolute top-10 left-10 w-px origin-top bg-foreground"
              style={{ height: `calc((100% - 80px) * ${progress})` }}
            />

            {STEPS.map((step) => (
              <div
                className="relative grid pb-10 last:pb-0"
                key={step.name}
                style={{ gridTemplateColumns: "80px 1fr", gap: "0 40px" }}
              >
                {/* Icon — plain white square, no circle, 80×80 with 16px padding */}
                <div className="relative z-10 flex h-20 w-20 shrink-0 items-center justify-center bg-white p-4">
                  {step.icon}
                </div>
                {/* Content */}
                <div className="space-y-1 pt-4">
                  <h3 className="heading-5 font-bold">{step.name}</h3>
                  <p className="body-base text-muted-foreground">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
